import type { GooglePlaceDto } from '@up2stay/up2promo-api-client';
import { LocationsApi } from '@up2stay/up2promo-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  hasAddressContent,
  toAddressDetailsDto,
} from '@/core/components/locations/location-create-payload';
import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import {
  ConfigurePhase,
  type ConfigurePhaseId,
} from '@/routes/app/products-setup-wizard/products-setup-configure-flow';

type NestedLocationLabels = {
  noWorkspace?: string;
  nameRequired?: string;
  createFailed?: string;
  createSuccess?: string;
};

type UseProductsSetupNestedLocationOptions = {
  workspaceId: string | undefined;
  labels: NestedLocationLabels;
  setDirection: (d: 1 | -1) => void;
  setConfigurePhase: (phase: ConfigurePhaseId) => void;
  onLocationCreated: (locationId: string) => void;
};

export function useProductsSetupNestedLocation({
  workspaceId,
  labels,
  setDirection,
  setConfigurePhase,
  onLocationCreated,
}: UseProductsSetupNestedLocationOptions) {
  const queryClient = useQueryClient();
  const [locationName, setLocationName] = useState('');
  const [locationNameError, setLocationNameError] = useState<string | null>(
    null,
  );
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(
    undefined,
  );
  const [locationLongitude, setLocationLongitude] = useState<
    number | undefined
  >(undefined);
  const [locationAddressDetails, setLocationAddressDetails] =
    useState<LocationAddressDetails | null>(null);
  const [locationGooglePlaceId, setLocationGooglePlaceId] = useState<
    string | undefined
  >(undefined);
  const [isCreatingLocation, setIsCreatingLocation] = useState(false);

  const resetNestedLocationForm = useCallback(() => {
    setLocationName('');
    setLocationNameError(null);
    setLocationLatitude(undefined);
    setLocationLongitude(undefined);
    setLocationAddressDetails(null);
    setLocationGooglePlaceId(undefined);
  }, []);

  const openCreateLocation = () => {
    resetNestedLocationForm();
    setDirection(1);
    setConfigurePhase(ConfigurePhase.CreateLocation);
  };

  const closeCreateLocation = () => {
    setDirection(-1);
    setConfigurePhase(ConfigurePhase.Location);
    resetNestedLocationForm();
  };

  const handlePlaceSelect = (place: GooglePlaceDto) => {
    const placeId = place.id?.trim();
    setLocationGooglePlaceId(placeId || undefined);
    if (!locationName.trim() && place.name) {
      setLocationName(place.name);
      setLocationNameError(null);
    }
  };

  const handleCreateLocation = async () => {
    if (!workspaceId) {
      toast.error(labels.noWorkspace);
      return;
    }
    if (!locationName.trim()) {
      const msg = labels.nameRequired;
      setLocationNameError(msg ?? null);
      toast.error(msg);
      return;
    }

    setIsCreatingLocation(true);
    try {
      const response = await LocationsApi.create({
        path: { workspaceId },
        body: {
          name: locationName.trim(),
          coordinates:
            locationLatitude !== undefined && locationLongitude !== undefined
              ? { latitude: locationLatitude, longitude: locationLongitude }
              : null,
          addressDetails: hasAddressContent(locationAddressDetails)
            ? toAddressDetailsDto(locationAddressDetails!)
            : null,
          ...(locationGooglePlaceId
            ? { reviewExternalIds: { google: locationGooglePlaceId } }
            : {}),
        },
      });

      const createdId = response.data?.id;
      if (!createdId) {
        toast.error(labels.createFailed);
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.root,
      });
      onLocationCreated(createdId);
      toast.success(labels.createSuccess);
      closeCreateLocation();
    } catch (createError) {
      console.error('Failed to create location:', createError);
      const message =
        (createError as { response?: { data?: { message?: string } } })
          ?.response?.data?.message ??
        (createError instanceof Error ? createError.message : null);
      toast.error(message ?? labels.createFailed);
    } finally {
      setIsCreatingLocation(false);
    }
  };

  return {
    locationName,
    setLocationName,
    locationNameError,
    setLocationNameError,
    locationLatitude,
    setLocationLatitude,
    locationLongitude,
    setLocationLongitude,
    locationAddressDetails,
    setLocationAddressDetails,
    locationGooglePlaceId,
    isCreatingLocation,
    resetNestedLocationForm,
    openCreateLocation,
    closeCreateLocation,
    handlePlaceSelect,
    handleCreateLocation,
  };
}
